import {
  CollectNodeFieldSchema,
  CollectNodeFieldTypeEnum,
} from "@aichat/flow-config";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../form";
import { Input } from "../../../input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../select";

import { MultiSelect } from "../../../multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../popover";
import { Button } from "../../../button";
import { cn } from "@aichat/ui/lib/utils";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../../../calendar";
import { useForm } from "react-hook-form";
import { dynamicFormResolver } from "./dynamic-form-resolver";
import { MessageEmit, WidgetConfig } from "@aichat/types/index";
import { ScrollArea } from "../../../scroll-area";
import { format } from "date-fns";

type DynamicFormProps = {
  fields: CollectNodeFieldSchema[];
  submitButtonText: string;
  isLoading?: boolean;
  // eslint-disable-next-line no-unused-vars
  handleSendForm?: (data: MessageEmit["message"]["form_response"]) => void;
  brandColor: WidgetConfig["brandColor"];
  textColor: WidgetConfig["textColor"];
  scrollHeight: string;
  onSuccess?: () => void;
};

export function extractPhoneParts(
  fullPhone: string,
  allowedCodes: string[] = [],
): {
  code: string;
  number: string;
} {
  const code =
    allowedCodes.find((c) => fullPhone.startsWith(c)) || allowedCodes[0] || "";
  const number = fullPhone.slice(code.length);
  return { code, number };
}

export function buildPhoneNumber(code: string, number: string): string {
  return `${code}${number}`;
}

function DynamicForm(props: DynamicFormProps) {
  const {
    fields,
    handleSendForm,
    isLoading,
    brandColor,
    textColor,
    scrollHeight,
    onSuccess,
  } = props;
  // Create default value from field.slot_name: value
  const defaultValues: Record<string, string> = fields.reduce(
    (acc, field) => {
      acc[field.slot_name!] = "";
      return acc;
    },
    {} as Record<string, string>,
  );

  const collectNodeForm = useForm({
    defaultValues: defaultValues,
    resolver: dynamicFormResolver(fields),
  });

  function handleSubmit(data: MessageEmit["message"]["form_response"]) {
    handleSendForm?.(data);
    onSuccess?.();
  }

  const brandColorHSLA = `hsla(${brandColor.h}, ${brandColor.s}%, ${brandColor.l}%, ${brandColor.a ?? 1})`;

  return (
    <Form {...collectNodeForm}>
      <form
        onSubmit={collectNodeForm.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <ScrollArea className={scrollHeight}>
          <div className="flex flex-col gap-4">
            {fields.map((collectNodeField) => {
              switch (collectNodeField.type) {
                case CollectNodeFieldTypeEnum.TEXT:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={collectNodeField.placeholder}
                              {...field}
                              value={field.value as string}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                case CollectNodeFieldTypeEnum.EMAIL:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="eg. joe@aichat.com"
                              {...field}
                              value={field.value as string}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                case CollectNodeFieldTypeEnum.NUMBER:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="eg. 123"
                              type="number"
                              {...field}
                              value={field.value as string}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                case CollectNodeFieldTypeEnum.PHONE: {
                  const allowedCountryCodes =
                    collectNodeField.countryCodes || [];

                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => {
                        const { code, number } = extractPhoneParts(
                          field.value.toString() || "",
                          allowedCountryCodes,
                        );

                        const handleCodeChange = (newCode: string) => {
                          const newPhone = buildPhoneNumber(newCode, number);
                          field.onChange(newPhone);
                        };

                        const handleNumberChange = (newNumber: string) => {
                          const newPhone = buildPhoneNumber(code, newNumber);
                          field.onChange(newPhone);
                        };

                        return (
                          <FormItem className="space-y-1">
                            <FormLabel>{collectNodeField.name}</FormLabel>
                            <div className="flex gap-2">
                              <Select
                                value={code}
                                onValueChange={handleCodeChange}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="Code" />
                                </SelectTrigger>
                                <SelectContent>
                                  {allowedCountryCodes.map((code) => (
                                    <SelectItem key={code} value={code}>
                                      {code}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormControl>
                                <Input
                                  type="tel"
                                  placeholder="Enter number"
                                  value={number}
                                  onChange={(e) =>
                                    handleNumberChange(e.target.value)
                                  }
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  );
                }

                case CollectNodeFieldTypeEnum.SELECT: {
                  if (collectNodeField.singleSelect)
                    return (
                      <FormField
                        key={collectNodeField.slot_name!}
                        name={collectNodeField.slot_name!}
                        control={collectNodeForm.control}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel>{collectNodeField.name}</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value as string}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {collectNodeField.selectValues.map((item) => (
                                  <SelectItem
                                    key={item.value}
                                    value={item.value}
                                  >
                                    {item.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    );
                  else
                    return (
                      <FormField
                        key={collectNodeField.slot_name!}
                        name={collectNodeField.slot_name!}
                        control={collectNodeForm.control}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel>{collectNodeField.name}</FormLabel>
                            <FormControl>
                              <MultiSelect
                                options={collectNodeField.selectValues.map(
                                  (item) => ({
                                    label: item.value,
                                    value: item.value,
                                  }),
                                )}
                                onValueChange={field.onChange}
                                {...field}
                                value={field.value as string[]}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    );
                }
                case CollectNodeFieldTypeEnum.DATE:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground",
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value as string, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={new Date(field.value as string)}
                                  onSelect={field.onChange}
                                  captionLayout="dropdown"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                case CollectNodeFieldTypeEnum.TIME:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="eg. 08:00 AM"
                              type="time"
                              {...field}
                              value={field.value as string}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                case CollectNodeFieldTypeEnum.LOCATION:
                  return (
                    <FormField
                      key={collectNodeField.slot_name!}
                      name={collectNodeField.slot_name!}
                      control={collectNodeForm.control}
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel>{collectNodeField.name}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="eg. Singapore"
                              {...field}
                              value={field.value as string}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
              }
            })}
          </div>
        </ScrollArea>
        <Button
          loading={isLoading}
          type="submit"
          style={{
            backgroundColor: brandColorHSLA,
            color: textColor === "dark" ? "black" : "white",
          }}
        >
          {props.submitButtonText}
        </Button>
      </form>
    </Form>
  );
}

export default DynamicForm;
